"use client"
import React, { useMemo } from "react";
import { Marker } from "react-map-gl/mapbox";
import moment from "moment";
import MapBoxMapPopup from "@/components/shared/MapBoxMapPopup";
import useUtils from "@/components/shared/useUtils";
import { useConfig } from "@/lib/providers/ConfigProvider";

import "@/components/locator/LocatorEventLayer.css";

const formatEventDate = event => {
  const date = moment(event.nextOccurrence ?? event.startDate);
  return event.startDateAllDay ? date.format("ddd, MMM D") : date.format("ddd, MMM D [at] h:mm A");
};

export const LocatorEventPopup = ({ resourceName, entry, event }) => (
  <MapBoxMapPopup latitude={event.latitude} longitude={event.longitude} anchor="bottom">
    <div className="locator-event-popup">
      {entry.label && <div className="locator-event-popup-label">{entry.label}</div>}
      <h5>{event.title}</h5>
      {event.subtitle && <p>{event.subtitle}</p>}
      <p>
        {formatEventDate(event)}
        {(event.locationName || event.address) && (
          <>
            <br />
            {event.locationName ?? event.address}
          </>
        )}
      </p>
      <a href={`/${resourceName}/events/${event.id}`} target="_blank" rel="noopener noreferrer">
        Event details
      </a>
    </div>
  </MapBoxMapPopup>
);

// Circular markers for the locator's upcoming events layer, one icon per event entry.
// eventEntries: [{ id, label, iconAssetId, events: [{ id, latitude, longitude, ... }] }]
export default function useLocatorEventMarkers(eventEntries, selectedEventId, onEventClicked) {
  const { apiUrl } = useConfig();
  const utils = useUtils();

  return useMemo(
    () =>
      (eventEntries ?? []).flatMap(entry =>
        (entry.events ?? []).map(event => (
          <Marker
            key={`locator-event-${entry.id}-${event.id}`}
            longitude={event.longitude}
            latitude={event.latitude}
            anchor="center"
            onClick={evt => {
              evt.originalEvent?.stopPropagation();
              onEventClicked?.(entry, event);
            }}
          >
            <div
              className={`locator-event-marker${selectedEventId === event.id ? " selected" : ""}`}
              title={event.title}
            >
              <img
                src={
                  entry.iconAssetId
                    ? `${apiUrl}/api/files/${entry.iconAssetId}/sq/thumb`
                    : utils.markerUrl("DE29E2", "calendar", "CIRCLE")
                }
                alt=""
              />
            </div>
          </Marker>
        ))
      ),
    [eventEntries, selectedEventId, onEventClicked, apiUrl]
  );
}
